import { gql } from "@apollo/client";

export const enforcementIntentSummaryFragment = gql`
  fragment enforcementIntentSummaryFragment on enforcement_intent {
    id
    code
    status
    source
    noticeNumber: notice_number
    noticeType: type
    timezone
    issuedAt: issued_at
    totalAmountDue: total_amount_due
    licensePlateNumber: license_plate_number
    licensePlateRegion: license_plate_region
    violationType: violation_type
    voidedReason: voided_reason
    reviews {
      id
      type
      status
      deniedReason: denied_reason
      requestedAt: requested_at
      reviewedAt: reviewed_at
    }
    sessions(order_by: [{ started_at: asc }]) {
      id
      streetAddress: street_address
      park {
        id
        startTime: start_time
        endTime: end_time
        durationSec: duration_sec
      }
      # No id and no parkFragment here: app_user's allow list on double_parks is
      # only start_time/end_time (+ the *_local computed fields), and a field
      # outside the allow list fails the whole query silently. Mirrors the same
      # selection in enforcementIntentDetailFragment. Added for AUT-5763 — ~12%
      # of MBPA intents are double-park-backed, and without this they resolve to
      # "no live signal" and render blank rather than tow-eligible.
      doublePark: double_park {
        startTime: start_time
        endTime: end_time
      }
      transaction {
        id
        account {
          id
          organizationName: organization_name
        }
      }
    }
  }
`;

/**
 * Mirrors `IntentVoidedReason` in the api repo (`dao/enforcement/intent.go`),
 * which is FK-constrained to the `enforcement.intent_voided_reason` table.
 *
 * Keep this union exhaustive against that enum. `enforcementIntentVoidedReasonLabels`
 * below is a `Record` over it, so every value listed here is required to have a
 * label — which means widening the union is what forces the label to exist and
 * turns a missing one into a compile error rather than a silent "N/A" in the
 * grid. When a value is added to the Go enum, add it here and the build will
 * tell you what else to do. See AUT-5544.
 */
export type EnforcementIntentSource = "automated" | "officer_input";

export type EnforcementIntentVoidedReason =
  | "internal_review"
  | "officer_review"
  | "ro_lookup_failure"
  | "dispute"
  | "mailing_failure"
  | "unmet_session_duration_requirement"
  | "session_aborted"
  | "review_period_expired"
  | "escalated_to_citation"
  | "client_request";

export const enforcementIntentVoidedReasonLabels: Record<EnforcementIntentVoidedReason, string> = {
  internal_review: "Internal Review",
  officer_review: "Officer Review",
  ro_lookup_failure: "RO Lookup Failure",
  dispute: "Dispute",
  mailing_failure: "Mailing Failure",
  unmet_session_duration_requirement: "Session too short",
  session_aborted: "Incomplete Evidence",
  review_period_expired: "Review Expired",
  escalated_to_citation: "Escalated to Citation",
  client_request: "Client Request",
};

export interface EnforcementIntentSummaryPark {
  id: string;
  startTime: string;
  // NULL while the park is live — see AUT-5502.
  endTime: string | null;
  durationSec: number | null;
}

/**
 * Narrower than `EnforcementIntentSummaryPark` on purpose: app_user may read
 * only start_time/end_time on double_parks, so there is no id to expose.
 */
export interface EnforcementIntentSummaryDoublePark {
  startTime: string;
  endTime: string | null;
}

export interface EnforcementIntentSummary {
  id: string;
  code: string | null;
  status: EnforcementIntentStatus;
  source: EnforcementIntentSource;
  noticeNumber: string;
  noticeType: EnforcementIntentType;
  timezone: string;
  issuedAt: string;
  totalAmountDue: number;
  licensePlateNumber: string;
  licensePlateRegion: string | null;
  violationType: string; // TODO: enum
  voidedReason: EnforcementIntentVoidedReason | null;
  reviews: {
    id: string;
    type: string; // TODO: enum
    status: string; // TODO: enum
    deniedReason: string | null;
    requestedAt: string;
    reviewedAt: string | null;
  }[];
  sessions: {
    id: string;
    streetAddress: string;
    park: EnforcementIntentSummaryPark | null;
    doublePark: EnforcementIntentSummaryDoublePark | null;
    transaction: {
      id: string;
      account: { id: string; organizationName: string } | null;
    } | null;
  }[];
}

export type EnforcementIntentStatus =
  | "requires_internal_review"
  | "requires_officer_review"
  | "requires_ro_lookup"
  | "requires_mailing"
  | "requires_payment"
  | "requires_dispute_resolution"
  | "paid"
  | "voided"
  | "sent_to_collections"
  | "notification_delivered"
  | "awaiting_ro_lookup_result"
  | "awaiting_initial_letter"
  | "requires_notice_pdf"
  | "awaiting_notice_pdf";

export type EnforcementIntentSimpleStatus = "pending" | "issued" | "voided";

export const enforcementIntentSimpleStatus: Record<EnforcementIntentStatus, EnforcementIntentSimpleStatus> = {
  requires_internal_review: "pending",
  requires_officer_review: "pending",
  requires_ro_lookup: "pending",
  requires_mailing: "issued",
  requires_payment: "issued",
  requires_dispute_resolution: "issued",
  paid: "issued",
  voided: "voided",
  sent_to_collections: "issued",
  notification_delivered: "issued",
  awaiting_ro_lookup_result: "issued",
  awaiting_initial_letter: "issued",
  requires_notice_pdf: "issued",
  awaiting_notice_pdf: "issued",
};

export type EnforcementIntentType = "citation" | "invoice" | "undecided";
